// scheduling/components/GenTrackFlag.jsx
// Advisory chip on staff-suggestion surfaces: this General-track candidate is out of
// tax/assurance balance, AND this job is on the side that would help (Trello #309).
//
// DJB: "Would also be great if the staff suggestions could include a flag for 'Needs
// more Tax' or 'Needs more Assurance'."
//
// ─── WHY IT ONLY FIRES WHEN THE JOB HELPS ───────────────────────────────────
// Showing "Needs more Tax" on an audit job would be true but useless — it is not a
// reason to pick that person for THAT job, and a badge that appears on candidates you
// shouldn't pick trains people to ignore it. So the flag renders only when the job's own
// side is the side the candidate is short of, which turns it into an argument FOR
// choosing them. That gate lives in helpsBalance() in utils/genTrack.js, shared with
// the Reporting tab so the two can never disagree.
//
// Renders nothing for non-General staff, for anyone in balance, or when there are too
// few hours to judge — bandOf() applies the same significance floor as the report.
//
// Mirrors NicheGapFlag's shape; palette matches the report's band colours so a "Needs
// Tax" chip here is recognisably the same verdict shown there.

import { BANDS, BAND_META, bandOf, helpsBalance, MIN_ACTUAL_HOURS } from '../utils/genTrack';

/**
 * @param {object} band       { taxPct, totalHours } from fetchGenTrackBands, or undefined
 * @param {string} deptCode   candidate's DepartmentCode — only 'GENERAL' is flagged
 * @param {'Tax'|'Assurance'|'Neither'} jobBucket  which side this job counts toward
 */
export default function GenTrackFlag({ band, deptCode, jobBucket, style }) {
  // Gen Track is the only population the balance concept applies to. A Tax-department
  // staffer doing 100% tax is not "out of balance", they are correctly deployed.
  if (deptCode !== 'GENERAL' || !band) return null;

  const verdict = bandOf(band.taxPct, band.totalHours, MIN_ACTUAL_HOURS);
  if (verdict === BANDS.BALANCED || verdict === BANDS.UNKNOWN) return null;
  if (!helpsBalance({ band: verdict }, jobBucket)) return null;

  const m = BAND_META[verdict];
  const wants = verdict === BANDS.NEEDS_TAX ? 'tax' : 'assurance';

  return (
    <span
      className="sched-sug-gentrack-flag"
      title={
        `General track: ${band.taxPct}% of their ${Math.round(band.totalHours)}h of client work `
        + `(charged plus already scheduled) is tax, outside the 40–60% in-balance band. `
        + `This is ${wants} work, so assigning it moves them toward balance.`
      }
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: m.bg, color: m.fg, border: `1px solid ${m.bar}`,
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
        whiteSpace: 'nowrap', cursor: 'help', ...style,
      }}
    >
      ⇅ {m.short}
    </span>
  );
}
